import { FunctionComponent, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserReferralListQuery } from '../../../../../services';
import { OrderDirection } from '../../../../../shared';
import { ChartWrapper, DataRowItem, Loader, TableChart, TableChartHeader } from '../../helpers';
import { ReferralProps } from '../Referral';

enum UserReferralListOrderBy {
  REFERRAL_COUNT = 'REFERRAL_COUNT',
}

export const UserReferralList: FunctionComponent<ReferralProps> = memo(({ from, to }) => {
  const { t } = useTranslation();

  const [orderBy, setOrderBy] = useState<string>(UserReferralListOrderBy.REFERRAL_COUNT);

  const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.DESC);

  const [page, setPage] = useState<number>(1);

  const { data: userReferralList, isFetching: isFetchingUserReferralList } =
    useUserReferralListQuery({
      from,
      to,
      orderDirection,
      page,
    });

  const renderChart = useMemo(() => {
    if (!userReferralList) {
      return <Loader />;
    }

    const { items, pageInfo } = userReferralList;

    const data: DataRowItem[][] = items.map(({ value, payload }) => {
      const { user } = payload;

      const { screenName } = user;

      return [{ value: screenName }, { value }];
    });

    const headers: TableChartHeader[] = [
      { label: t('userReferralListLabels.name') },
      { label: t('userReferralListLabels.value'), orderBy: UserReferralListOrderBy.REFERRAL_COUNT },
    ];

    return (
      <TableChart
        sticky
        headers={headers}
        data={data}
        pageInfo={pageInfo}
        setPage={setPage}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        orderDirection={orderDirection}
        setOrderDirection={setOrderDirection}
        loading={isFetchingUserReferralList}
      />
    );
  }, [isFetchingUserReferralList, orderBy, orderDirection, t, userReferralList]);

  return (
    <ChartWrapper
      typeLocaliseKey={'chartTypes.user-referral-list'}
      infoLocaliseKey={'userReferralList.info'}
    >
      {renderChart}
    </ChartWrapper>
  );
});
